import { z } from 'zod';
import { AppointmentSource, AppointmentStatus } from '../enums.js';
import { CallerMessageSchema } from './call.js';
import { E164, TimestampSchema } from '../primitives.js';
import { ServiceAnswersSchema } from './serviceQuestions.js';
/** `businesses/{businessId}/appointments/{appointmentId}` */
export const AppointmentSchema = z.object({
    serviceId: z.string().min(1),
    /** Denormalised for display without a services join. */
    serviceName: z.string().min(1),
    start: TimestampSchema,
    end: TimestampSchema,
    customerName: z.string().min(1).max(120),
    customerPhone: E164.nullable().default(null),
    /**
     * One short thing the caller said that the booking fields cannot hold —
     * "it's for the spaniel", "I'll be 10 minutes late", "same colour as last
     * time" (doc 23 P1).
     *
     * Written by the assistant from the conversation, and editable by the owner
     * afterwards. Deliberately ONE free-text line and not a structured field:
     * the whole point is to catch what the schema did not anticipate, and the
     * moment it has a shape it stops catching anything else.
     *
     * Never fed back into the AI prompt — same rule as the client's `notes`
     * (doc 11 §9). It is a note for the human reading the calendar.
     */
    note: z.string().max(300).default(''),
    /**
     * What the caller asked the business to know or do (doc 28) — copied from the
     * call that booked this appointment, and carried across a reschedule because
     * it is the same booking.
     *
     * NOT the same as `note` above, and the difference is the reason both exist:
     * the note is about the booking ("for the spaniel"), the message is about the
     * caller's problem ("brakes squealing since the weekend, wants it looked at
     * while the car is in"). A message also exists on calls that booked nothing,
     * which is why it lives on the call record first and is denormalised here
     * (doc 28 D2) — the dashboard lists appointments, and a join per row to find
     * out whether anyone left a message is a cost paid on every render forever.
     */
    message: CallerMessageSchema.nullable().default(null),
    /**
     * The service's questions and the customer's answers, as they were when the
     * booking was made (doc 64): question text is the owner's wording AT THAT
     * TIME, so editing or removing the service never rewrites it. Server-written
     * only, together with the appointment. A move to another time keeps it; a
     * move to another service replaces it. Empty on bookings made before
     * questions existed, and for services that ask none.
     */
    serviceAnswers: ServiceAnswersSchema.default([]),
    status: AppointmentStatus.default('confirmed'),
    source: AppointmentSource.default('ai_call'),
    /** Link to the call record that created it (also the idempotency key). */
    callId: z.string().nullable().default(null),
    /** Created by the owner's browser "Test call" rather than a real customer
     * (doc 13a §6). It IS a real appointment holding a real slot — that is the
     * point of the test — but it must not feed the client base or the numbers the
     * owner judges the assistant by, so it is flagged at the source and filtered
     * downstream. Mirrors `CallSchema.test`. */
    test: z.boolean().default(false),
    /** Assigned staff member; null = legacy/unassigned — blocks ALL staff (doc 09 D4). */
    staffId: z.string().nullable().default(null),
    /** Denormalised for display without a staff join (same pattern as serviceName). */
    staffName: z.string().nullable().default(null),
    createdAt: TimestampSchema,
    cancelledAt: TimestampSchema.optional(),
    /** Why it was cancelled — `'rescheduled'` when superseded by a move (doc 14 D3).
     * Absent on owner cancellations from the portal, which write status only. */
    cancelReason: z.string().max(200).optional(),
    /**
     * The call that cancelled it, or null when the OWNER did from the portal
     * (doc 22 D2).
     *
     * `callId` records who CREATED the appointment and keeps doing so, which is
     * why this is a separate field: an appointment booked last week and cancelled
     * on today's call carries last week's `callId` forever. Without this, a
     * cancellation from a live conversation is indistinguishable from one made in
     * the browser — and those must behave differently, because only one of them
     * has a conversation still running that could undo it.
     */
    cancelledByCallId: z.string().nullable().default(null),
    /** Reschedule audit chain (doc 14 D3): the appointment this one replaced… */
    rescheduledFromId: z.string().nullable().default(null),
    /** …and the one that replaced it. Set on the cancelled side of a move; also
     * the replay marker that makes a retried reschedule idempotent (doc 14 §5). */
    rescheduledToId: z.string().nullable().default(null),
});
export const AppointmentWithIdSchema = AppointmentSchema.extend({ id: z.string() });
//# sourceMappingURL=appointment.js.map